import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { deleteField } from '@angular/fire/firestore';
import { SUPPORTED_APS_FORM_TYPES } from '@models/document-processing-form-details';
import { Matter } from '@models/matter';
import { MatterDocumentType } from '@models/matter-file';
import { Store } from '@ngxs/store';
import { DocumentProcessorService } from '@services/document-ai/document-processor.service';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterAiJobServiceRepo } from '@repos/matter-ai-job/matter-ai-job.service';
import { combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
	NOTE_TYPES,
	CALL_TO_ACTION_TYPES,
} from 'src/app/ui/note/note.component';

@Component({
	selector: 'app-ai-document-processing-disclaimer',
	templateUrl: './ai-document-processing-disclaimer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AiDocumentProcessingDisclaimerComponent {
	private _matterDocumentType: MatterDocumentType;
	SUPPORTED_APS_FORM_TYPES = SUPPORTED_APS_FORM_TYPES;
	NOTE_TYPES = NOTE_TYPES;
	CLOSE_TYPE = CALL_TO_ACTION_TYPES.CLOSE;
	showDisclaimer$ = of(false);
	isSupported$ = of(false);
	isFailed$ = of(false);
	noteType$;
	title$;
	body$;

	@Input() set matterDocumentType(matterDocumentType: MatterDocumentType) {
		this._matterDocumentType = matterDocumentType;

		// Show disclaimer when not loading and AI job exists (succeeded or failed)
		this.showDisclaimer$ = combineLatest([
			this.documentProcessorService.isLoading$(matterDocumentType),
			this.store.select(SelectedMatter.getSelected).pipe(
				map((matter) => {
					const aiJobId = this._getAiJobId(matter, matterDocumentType);
					return !!aiJobId;
				}),
			),
		]).pipe(map(([isLoading, hasAiJob]) => !isLoading && hasAiJob));

		this.isSupported$ =
			matterDocumentType === MatterDocumentType.APS
				? this.store
						.select(SelectedMatter.getDocumentProcessingFormDetails)
						.pipe(
							map((cb) => cb(matterDocumentType)),
							map((formDetails) =>
								formDetails?.some((fd) =>
									SUPPORTED_APS_FORM_TYPES.includes(fd.formType),
								),
							),
						)
				: of(true);

		// Detect FAILED AI job status
		this.isFailed$ = this.store.select(SelectedMatter.getSelected).pipe(
			switchMap((matter) => {
				const aiJobId = this._getAiJobId(matter, matterDocumentType);
				if (!aiJobId || !matter?.id) {
					return of(false);
				}

				return this.matterAiJobService
					.Get$(aiJobId, { matterId: matter.id })
					.pipe(map((job) => job?.status === 'failed'));
			}),
		);

		this.noteType$ = combineLatest([this.isSupported$, this.isFailed$]).pipe(
			map(([isSupported, isFailed]) => {
				if (isFailed) return NOTE_TYPES.ERROR;
				return isSupported ? NOTE_TYPES.INFO : NOTE_TYPES.WARNING;
			}),
		);

		this.title$ = combineLatest([this.isSupported$, this.isFailed$]).pipe(
			map(([isSupported, isFailed]) => {
				if (isFailed) return 'Manual Entry Required';
				return isSupported
					? 'Generated by AI: Review for Confidence'
					: 'Review Required';
			}),
		);

		this.body$ = combineLatest([this.isSupported$, this.isFailed$]).pipe(
			map(([isSupported, isFailed]) => {
				if (isFailed) {
					return 'Something went wrong and we were unable to extract details from your document. Please enter them manually.';
				}
				return isSupported
					? 'AI accuracy is great, but not guaranteed. Verify the results below to be sure.'
					: 'The uploaded APS type is not fully supported. Please review the results carefully and cross-check with the original document.';
			}),
		);
	}

	private _getAiJobId(
		matter: Matter,
		documentType: MatterDocumentType,
	): string | null {
		switch (documentType) {
			case MatterDocumentType.APS:
				return matter?.latestAPS?.aiJobId || null;
			case MatterDocumentType.FIRE_INSURANCE:
				return matter?.fireInsurance?.aiJobId || null;
			default:
				return null;
		}
	}

	close() {
		this.matterDispatch.OverwriteFields(
			`documentProcessingMetaData.${this._matterDocumentType}.showAccuracyDisclaimer`,
			deleteField(),
		);
	}

	constructor(
		private store: Store,
		private matterDispatch: MatterDispatch,
		private documentProcessorService: DocumentProcessorService,
		private matterAiJobService: MatterAiJobServiceRepo,
	) {}
}
